import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatMinorUnits, formatPercent, formatQuantity } from '../reportFormatters';
import { CommercialValue } from './CommercialValue';
import { PeriodTrendsOverview } from './PeriodTrendsOverview';

function PeriodLabel({ bucket }) {
  if (!bucket) {
    return <>—</>;
  }

  return (
    <>
      <strong>{bucket.label || bucket.key || '—'}</strong>
      <span className="report-periods__range">
        {bucket.date_from === bucket.date_to
          ? bucket.date_from
          : `${bucket.date_from || '—'} – ${bucket.date_to || '—'}`}
      </span>
    </>
  );
}

function MarketingPeak({ bucket, kind }) {
  const quantity = (bucket && bucket.fulfilled_quantity) || {};
  const orders = (bucket && bucket.orders) || {};

  return (
    <article className="report-periods__peak-card" data-testid={`period-peak-${kind}`}>
      <span className="report-periods__peak-label">
        <FormattedMessage id={`components.pages.reports.periods.peaks.${kind}`}/>
      </span>
      <div className="report-periods__peak-period"><PeriodLabel bucket={bucket}/></div>
      {kind === 'quantity' ? (
        <>
          <strong className="report-periods__peak-value">{formatQuantity(quantity.net)}</strong>
          <span className="report-periods__peak-details">
            <FormattedMessage id="components.pages.reports.periods.purchased"/>: {formatQuantity(quantity.purchased)}
            {' · '}<FormattedMessage id="components.pages.reports.periods.direct"/>: {formatQuantity(quantity.direct_allocated)}
            {' · '}<FormattedMessage id="components.pages.reports.periods.refunded"/>: {formatQuantity(quantity.refunded)}
          </span>
        </>
      ) : (
        <>
          <strong className="report-periods__peak-value">{formatQuantity(orders.total)}</strong>
          <span className="report-periods__peak-details">
            <FormattedMessage id="components.pages.reports.periods.billable"/>: {formatQuantity(orders.billable)}
            {' · '}<FormattedMessage id="components.pages.reports.periods.direct"/>: {formatQuantity(orders.direct_allocation)}
          </span>
        </>
      )}
    </article>
  );
}

function FinancialPeak({ bucket }) {
  const netSales = (bucket && bucket.net_sales) || null;
  const directAllocationValue = (bucket && bucket.direct_allocation_value) || null;
  const commercialValue = (bucket && bucket.commercial_value) || netSales;

  return (
    <article className="report-periods__peak-card" data-testid="period-peak-revenue">
      <span className="report-periods__peak-label">
        <FormattedMessage id="components.pages.reports.periods.peaks.commercialValue"/>
      </span>
      <div className="report-periods__peak-period"><PeriodLabel bucket={bucket}/></div>
      <strong className="report-periods__peak-value">{formatMinorUnits(commercialValue, 'without_vat')}</strong>
      <span className="report-periods__peak-details">
        <FormattedMessage id="components.pages.reports.money.paidNet"/>: {formatMinorUnits(netSales)}
        {' · '}<FormattedMessage id="components.pages.reports.money.directAllocation"/>: {formatMinorUnits(directAllocationValue)}
      </span>
      <span className="report-periods__peak-details">
        TVA: {formatMinorUnits(commercialValue, 'vat')}
        {' · '}<FormattedMessage id="components.pages.reports.periods.withVat"/>: {formatMinorUnits(commercialValue, 'with_vat')}
      </span>
    </article>
  );
}

function MoneyCell({ commercialValue, directAllocationValue, money, showCommercial = false }) {
  if (showCommercial) {
    return (
      <>
        <CommercialValue commercialValue={commercialValue} directAllocationValue={directAllocationValue} netSales={money}/>
        <span className="report-periods__cell-details">TVA: {formatMinorUnits(commercialValue, 'vat')}</span>
        <span className="report-periods__cell-details"><FormattedMessage id="components.pages.reports.periods.withVat"/>: {formatMinorUnits(commercialValue, 'with_vat')}</span>
      </>
    );
  }
  return (
    <>
      <strong>{formatMinorUnits(money, 'without_vat')}</strong>
      <span className="report-periods__cell-details">TVA: {formatMinorUnits(money, 'vat')}</span>
      <span className="report-periods__cell-details">
        <FormattedMessage id="components.pages.reports.periods.withVat"/>: {formatMinorUnits(money, 'with_vat')}
      </span>
    </>
  );
}

function MarketingTable({ labels, series }) {
  return (
    <table className="report-periods__table report-periods__table--marketing">
      <caption className="sr-only"><FormattedMessage id="components.pages.reports.periods.marketingTable"/></caption>
      <colgroup><col/><col/><col/></colgroup>
      <thead>
        <tr>
          <th scope="col">{labels.period}</th>
          <th scope="col">{labels.quantity}</th>
          <th scope="col">{labels.orders}</th>
        </tr>
      </thead>
      <tbody>
        {series.map(bucket => {
          const quantity = bucket.fulfilled_quantity || {};
          const orders = bucket.orders || {};
          return (
            <tr data-testid={`period-row-${bucket.key}`} key={bucket.key}>
              <td data-label={labels.period}>
                <div className="report-periods__cell-content"><PeriodLabel bucket={bucket}/></div>
              </td>
              <td data-label={labels.quantity}>
                <div className="report-periods__cell-content">
                  <strong>{formatQuantity(quantity.net)}</strong>
                  <span className="report-periods__cell-details">
                    <FormattedMessage id="components.pages.reports.periods.purchased"/>: {formatQuantity(quantity.purchased)}
                    {' · '}<FormattedMessage id="components.pages.reports.periods.direct"/>: {formatQuantity(quantity.direct_allocated)}
                    {' · '}<FormattedMessage id="components.pages.reports.periods.refunded"/>: {formatQuantity(quantity.refunded)}
                  </span>
                </div>
              </td>
              <td data-label={labels.orders}>
                <div className="report-periods__cell-content">
                  <strong>{formatQuantity(orders.total)}</strong>
                  <span className="report-periods__cell-details">
                    <FormattedMessage id="components.pages.reports.periods.billable"/>: {formatQuantity(orders.billable)}
                    {' · '}<FormattedMessage id="components.pages.reports.periods.direct"/>: {formatQuantity(orders.direct_allocation)}
                  </span>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function FinancialTable({ labels, series }) {
  return (
    <table className="report-periods__table report-periods__table--financial">
      <caption className="sr-only"><FormattedMessage id="components.pages.reports.periods.financialTable"/></caption>
      <colgroup><col/><col/><col/><col/></colgroup>
      <thead>
        <tr>
          <th scope="col">{labels.period}</th>
          <th scope="col">{labels.gross}</th>
          <th scope="col">{labels.refunds}</th>
          <th scope="col">{labels.net}</th>
        </tr>
      </thead>
      <tbody>
        {series.map(bucket => (
          <tr data-testid={`period-row-${bucket.key}`} key={bucket.key}>
            <td data-label={labels.period}>
              <div className="report-periods__cell-content"><PeriodLabel bucket={bucket}/></div>
            </td>
            <td data-label={labels.gross}>
              <div className="report-periods__cell-content"><MoneyCell money={bucket.gross_sales}/></div>
            </td>
            <td data-label={labels.refunds}>
              <div className="report-periods__cell-content"><MoneyCell money={bucket.refunds}/></div>
            </td>
            <td data-label={labels.net}>
              <div className="report-periods__cell-content"><MoneyCell commercialValue={bucket.commercial_value} directAllocationValue={bucket.direct_allocation_value} money={bucket.net_sales} showCommercial/></div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PeriodsReport({
  activityData,
  activityError,
  activityLoading,
  data,
  error,
  lens,
  loading,
  onLensChange,
  onRetry,
  periodPreset,
  purchaseData,
  purchaseError,
  purchaseLoading
}) {
  const intl = useIntl();
  const result = data || {};
  const series = Array.isArray(result.series) ? result.series : [];
  const peaks = result.peaks || {};
  const comparisonChanges = result.comparison && result.comparison.changes
    ? result.comparison.changes
    : null;
  const meta = result.meta || {};
  const marketing = lens !== 'financial';
  const labels = {
    period: intl.formatMessage({ id: 'components.pages.reports.periods.period' }),
    quantity: intl.formatMessage({ id: 'components.pages.reports.periods.quantity' }),
    orders: intl.formatMessage({ id: 'components.pages.reports.periods.orders' }),
    gross: intl.formatMessage({ id: 'components.pages.reports.periods.gross' }),
    refunds: intl.formatMessage({ id: 'components.pages.reports.periods.refunds' }),
    net: intl.formatMessage({ id: 'components.pages.reports.periods.net' })
  };

  if (loading) {
    return (
      <div aria-busy="true" aria-live="polite" className="report-periods-loading" data-testid="periods-loading">
        <span/><span/><span/><span/>
        <span className="sr-only"><FormattedMessage id="components.pages.reports.periods.loading"/></span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="report-state report-state--error" role="alert">
        <FormattedMessage id="components.pages.reports.periods.error"/>
        <button className="btn btn-outline-primary" onClick={onRetry} type="button">
          <FormattedMessage id="components.pages.reports.periods.retry"/>
        </button>
      </div>
    );
  }

  if (!data || series.length === 0) {
    return <div className="report-state"><FormattedMessage id="components.pages.reports.periods.empty"/></div>;
  }

  return (
    <section aria-labelledby="periods-lens-title" className="report-periods">
      <header className="report-periods__header">
        <div>
          <h2 id="periods-lens-title">
            <FormattedMessage id={`components.pages.reports.periods.${marketing ? 'marketingTitle' : 'financialTitle'}`}/>
          </h2>
          <p><FormattedMessage id={`components.pages.reports.periods.${marketing ? 'marketingHint' : 'financialHint'}`}/></p>
        </div>
        <div aria-label={intl.formatMessage({ id: 'components.pages.reports.periods.lens' })} className="report-periods__lens" role="group">
          {['marketing', 'financial'].map(value => (
            <button
              aria-pressed={lens === value}
              className={lens === value ? 'active' : ''}
              key={value}
              onClick={() => onLensChange(value)}
              type="button"
            >
              <FormattedMessage id={`components.pages.reports.periods.lens.${value}`}/>
            </button>
          ))}
        </div>
      </header>

      <PeriodTrendsOverview
        activityData={activityData}
        activityError={activityError}
        activityLoading={activityLoading}
        periodData={data}
        periodPreset={periodPreset}
        purchaseData={purchaseData}
        purchaseError={purchaseError}
        purchaseLoading={purchaseLoading}
      />

      {marketing ? (
        <div aria-label={intl.formatMessage({ id: 'components.pages.reports.periods.marketingPeaks' })} className="report-periods__peaks">
          <MarketingPeak bucket={peaks.fulfilled_quantity} kind="quantity"/>
          <MarketingPeak bucket={peaks.orders} kind="orders"/>
          {comparisonChanges && (
            <article className="report-periods__comparison-card">
              <span><FormattedMessage id="components.pages.reports.periods.comparison"/></span>
              <strong>{formatPercent(comparisonChanges.fulfilled_quantity_percent)}</strong>
              <small><FormattedMessage id="components.pages.reports.periods.quantity"/></small>
              <strong>{formatPercent(comparisonChanges.orders_percent)}</strong>
              <small><FormattedMessage id="components.pages.reports.periods.orders"/></small>
            </article>
          )}
        </div>
      ) : (
        <div aria-label={intl.formatMessage({ id: 'components.pages.reports.periods.financialPeaks' })} className="report-periods__peaks">
          <FinancialPeak bucket={peaks.net_sales_without_vat}/>
          {comparisonChanges && (
            <article className="report-periods__comparison-card">
              <span><FormattedMessage id="components.pages.reports.periods.comparison"/></span>
              <strong>{formatPercent(comparisonChanges.net_sales_without_vat_percent)}</strong>
              <small><FormattedMessage id="components.pages.reports.periods.netWithoutVat"/></small>
            </article>
          )}
        </div>
      )}

      <div className="report-periods__table-wrap">
        {marketing
          ? <MarketingTable labels={labels} series={series}/>
          : <FinancialTable labels={labels} series={series}/>}
      </div>

      <footer className="report-periods__freshness">
        <span><FormattedMessage id="components.pages.reports.summary.updated"/>: {meta.indexed_at || '—'}</span>
        <span>{meta.timezone || 'Europe/Bucharest'} · {meta.granularity || 'auto'}</span>
        {Number(meta.fallback_payment_timestamp_count || 0) > 0 && (
          <span className="report-summary__freshness-warning">
            <FormattedMessage
              id="components.pages.reports.summary.fallbackTimestamps"
              values={{ count: meta.fallback_payment_timestamp_count }}
            />
          </span>
        )}
      </footer>
    </section>
  );
}

export { PeriodsReport };
